import type React from 'react';
import * as yup from 'yup';

import type { DynamicComponent } from '../dynamicComponent';
import { type CellComponent, type DefinitionComponent, strategiesType } from '../types';

import {
  type ChangeComponentProps,
  DateComponent,
  NumberWrapper,
  TextWrapper
} from './simpleFieldWrappers';

interface TaxReturnRuntime {
  readOnly: boolean;
}

export interface StrategyFactoryOptions {
  ChangeComponent?: React.ComponentType<ChangeComponentProps>;
  getRequiredMessage: () => string;
  useRuntime: () => TaxReturnRuntime;
}

const buildRequiredNumberValidation = (
  definition: DefinitionComponent,
  getRequiredMessage: () => string
) => {
  const validation: Record<string, yup.NumberSchema<number | null | undefined>> = {};
  validation[definition.code] = yup
    .number()
    .nullable()
    .required(getRequiredMessage())
    .typeError(getRequiredMessage());

  return yup.object().shape(validation);
};

export const createTextStrategy = ({
  ChangeComponent,
  getRequiredMessage,
  useRuntime
}: StrategyFactoryOptions): DynamicComponent => ({
  Component: (definition: DefinitionComponent) => {
    const { readOnly } = useRuntime();
    const def = definition as CellComponent;

    return (
      <TextWrapper
        readonly={!def.editable || readOnly}
        isFormula={def.isFormula}
        label={def.name}
        name={def.fieldPath ?? def.code}
        description={def.description}
        ChangeComponent={ChangeComponent}
      />
    );
  },
  CanGet: (type: string) => type === strategiesType.text,
  Validations: (definition: DefinitionComponent) => {
    const def = definition as CellComponent;
    const validation: Record<string, yup.StringSchema<string | undefined>> = {};

    if (def.mandatory) {
      validation[definition.code] = yup.string().required(getRequiredMessage());
    }

    return yup.object().shape(validation);
  }
});

export const createNumberStrategy = ({
  ChangeComponent,
  getRequiredMessage,
  useRuntime
}: StrategyFactoryOptions): DynamicComponent => ({
  Component: (definition: DefinitionComponent) => {
    const { readOnly } = useRuntime();
    const def = definition as CellComponent;

    return (
      <NumberWrapper
        readOnly={!def.editable || readOnly}
        isFormula={def.isFormula}
        label={def.name}
        includeThousandsSeparator={true}
        name={def.fieldPath ?? def.code}
        description={def.description}
        ChangeComponent={ChangeComponent}
      />
    );
  },
  CanGet: (type: string) => type === strategiesType.numeric,
  Validations: (definition: DefinitionComponent) => {
    const def = definition as CellComponent;

    if (!def.mandatory) {
      return yup.object().shape({});
    }

    return buildRequiredNumberValidation(definition, getRequiredMessage);
  }
});

export const createCurrencyStrategy = ({
  ChangeComponent,
  getRequiredMessage,
  useRuntime
}: StrategyFactoryOptions): DynamicComponent => ({
  Component: (definition: DefinitionComponent) => {
    const { readOnly } = useRuntime();
    const def = definition as CellComponent;

    return (
      <NumberWrapper
        readOnly={!def.editable || readOnly}
        isFormula={def.isFormula}
        label={def.name}
        name={def.fieldPath ?? def.code}
        description={def.description}
        allowDecimal={true}
        allowNegative={true}
        includeThousandsSeparator={true}
        ChangeComponent={ChangeComponent}
      />
    );
  },
  CanGet: (type: string) => type === strategiesType.currency,
  Validations: (definition: DefinitionComponent) => {
    const def = definition as CellComponent;

    if (!def.mandatory) {
      return yup.object().shape({});
    }

    return buildRequiredNumberValidation(definition, getRequiredMessage);
  }
});

export const createDateStrategy = ({
  ChangeComponent,
  getRequiredMessage,
  useRuntime
}: StrategyFactoryOptions): DynamicComponent => ({
  Component: (definition: DefinitionComponent) => {
    const { readOnly } = useRuntime();
    const def = definition as CellComponent;

    return (
      <DateComponent
        readOnly={!def.editable || readOnly}
        label={def.name}
        name={def.fieldPath ?? def.code}
        description={def.description}
        isFormula={def.isFormula}
        ChangeComponent={ChangeComponent}
      />
    );
  },
  CanGet: (type: string) => type === strategiesType.date,
  Validations: (definition: DefinitionComponent) => {
    const def = definition as CellComponent;
    const validation: Record<string, yup.DateSchema<Date | undefined>> = {};

    if (def.mandatory) {
      validation[definition.code] = yup.date().required(getRequiredMessage());
    }

    return yup.object().shape(validation);
  }
});
